import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useConfig } from "@/lib/useConfig";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Read-only network job visibility for the Central Sales Manager: pick a studio + a pipeline stage
// and see the jobs there. No editing / moving.
export const JobOverview = () => {
  const stages = useConfig("pipeline-stages");
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState("");
  const [stage, setStage] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/studios").then(({ data }) => setStudios(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!studioId || !stage) { setJobs([]); return; }
    setLoading(true);
    api.get("/jobs", { params: { studio_id: studioId, status: stage } })
      .then(({ data }) => setJobs(data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [studioId, stage]);

  const vehicleOf = (j) => {
    const v = j.vehicle || {};
    return [v.make, v.model, v.colour].filter(Boolean).join(" ") || j.vehicle_summary || "—";
  };

  return (
    <Card className="border-brand-line shadow-none" data-testid="job-overview">
      <CardHeader>
        <CardTitle className="text-lg">Job overview</CardTitle>
        <p className="text-xs text-slate-500">Read-only view of any studio's jobs by pipeline stage.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Studio</Label>
            <Select value={studioId} onValueChange={setStudioId}>
              <SelectTrigger className="mt-1" data-testid="job-overview-studio"><SelectValue placeholder="Select studio" /></SelectTrigger>
              <SelectContent>
                {studios.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Pipeline stage</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="mt-1" data-testid="job-overview-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>
                {stages.map((s) => (<SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!studioId || !stage ? (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="job-overview-empty">
            Pick a studio and a stage to see jobs.
          </p>
        ) : loading ? (
          <p className="py-6 text-center text-sm text-slate-400">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="job-overview-none">
            No jobs in this studio at "{stage}".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="job-overview-table">
              <thead>
                <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4">Customer</th>
                  <th className="py-2 pr-4">Vehicle</th>
                  <th className="py-2 pr-4">Job date</th>
                  <th className="py-2">Technician</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b border-brand-line/60" data-testid={`job-overview-row-${j.id}`}>
                    <td className="py-2 pr-4 font-medium text-brand-ink">{j.customer_name || "—"}</td>
                    <td className="py-2 pr-4">{vehicleOf(j)}</td>
                    <td className="py-2 pr-4">{j.scheduled_date || j.booked_date || (j.created_at || "").slice(0, 10) || "—"}</td>
                    <td className="py-2">{j.installer_name || j.assigned_to || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
